import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { categoryEvents } from '@/lib/category-events';

export interface Category {
  id: string;
  name: string;
  type: 'income' | 'expense';
  icon: string;
  color: string;
  is_system: boolean;
  parent_id: string | null;
  sort_order?: number;
  user_id: string | null;
}

export interface HierarchicalCategory extends Category {
  children: Category[];
}

export function useCategories(type?: 'income' | 'expense' | 'all') {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // RLS policies já incluem categorias do sistema, próprias e compartilhadas
      let query = supabase
        .from('categories')
        .select('*')
        .order('name');

      if (type && type !== 'all') {
        query = query.eq('type', type);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCategories((data as Category[]) || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Subscribe to category events to refresh when other components create categories
  useEffect(() => {
    const unsubscribe = categoryEvents.subscribe(() => {
      fetchCategories();
    });
    return unsubscribe;
  }, [fetchCategories]);

  // Organize categories hierarchically (parents with their children), sorted alphabetically
  const hierarchicalCategories: HierarchicalCategory[] = categories
    .filter(c => !c.parent_id)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
    .map(parent => ({
      ...parent,
      children: categories
        .filter(c => c.parent_id === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
    }));

  // Flat list for simple lookups
  const categoryMap = new Map(categories.map(c => [c.id, c]));

  const createCategory = async (data: {
    name: string;
    type: 'income' | 'expense';
    icon?: string;
    color?: string;
    parent_id?: string | null;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: newCategory, error } = await supabase
        .from('categories')
        .insert({
          user_id: user.id,
          name: data.name,
          type: data.type,
          icon: data.icon || 'CircleDot',
          color: data.color || '#6366f1',
          parent_id: data.parent_id || null,
          is_system: false
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setCategories(prev => [...prev, newCategory as Category]);

      // Notify other components to refresh their category lists
      categoryEvents.emit();

      toast({
        title: 'Categoria criada!',
        description: `${data.name} foi adicionada com sucesso.`
      });

      return newCategory as Category;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar categoria',
        description: error.message
      });
      return null;
    }
  };

  return {
    categories,
    hierarchicalCategories,
    categoryMap,
    loading,
    refetch: fetchCategories,
    createCategory
  };
}
